/**
 * Box 1, สถานะข้อมูลและโมเดล (spec §4.2, §5.1, §5.2, §5.12): the seven readiness checks, each ✓ / ✕ with its value; the
 * outcome selector, which writes only once a config exists (plan R3); with an active version, the four tiles and the
 * version line. A tile is red per QUALITY, on spec C9's operators, so "no tile red" is advice.ts's qualityOk and box 1
 * and box 7 never disagree (T12a). The data's last day is never red: an old version has its own note (C5).
 * Last, the training download for every role (spec §5.11, O13), enabled by the page's downloadAllowed, beside spec §6's
 * client-data line, which names a count outcome's units (spec C11), and the upload slot, which the page fills for canEdit
 * only (O14), under it the line naming the version it stored (I1).
 *
 * @module components/budget/ReadinessCard
 */
import React, { useId, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { OverviewCard } from '@/components/overview/OverviewBox';
import { OUTCOME_METRICS, QUALITY, READINESS, type OutcomeMetric } from '@/services/budget/budgetContract';
import type { Bundle } from '@/services/budget/bundleSchema';
import { clientDataLine } from '@/services/budget/outcomeUnits';
import type { CheckId, ReadinessCheck } from '@/services/budget/readinessRules';
import { fmtShortDate, type TFn } from '@/services/channels/channelFormat';
import { TONE_CLASS } from '@/services/looker/lookerDelta';
import { DASH, fmtPct } from '@/services/overview/overviewFormat';
import { platformShortLabel } from '@/utils/platformLabel';
import { checkName } from './InterimCard';

type Diagnostics = Bundle['diagnostics'];

const SELECT_CLASS =
  'rounded-lg border border-neutral-300 bg-white px-2 py-1 text-sm text-ink-900 '
  + 'focus:outline-none focus:ring-2 focus:ring-ink-950 disabled:opacity-60';
const BUTTON_CLASS = 'rounded-md bg-ink-950 px-3 py-1.5 text-sm font-medium text-white hover:bg-ink-900 disabled:opacity-50';
const PART_CLASS = 'mt-4 border-t border-neutral-100 pt-3';

/** onDownload's refusal: the route's words, and the check ids a 409 lists as failing, which the card names. */
export class DownloadRefused extends Error {
  readonly failing: readonly string[];
  constructor(message: string, failing: readonly string[]) {
    super(message);
    this.name = 'DownloadRefused';
    this.failing = failing;
  }
}

const names = (platforms: readonly string[]): string => platforms.map((p) => platformShortLabel(p)).join(' · ');

/** Literal keys, so the locale integrity test sees them; the thresholds are READINESS's. */
const checkLabel = (id: CheckId, metric: OutcomeMetric, t: TFn): string => {
  if (id === 'history') return t('budget.check.history', { n: READINESS.historyWeeks });
  if (id === 'overlap') return t('budget.check.overlap', { n: READINESS.overlapWeeks });
  if (id === 'channels') return t('budget.check.channels', { n: READINESS.minChannels });
  if (id === 'continuity') return t('budget.check.continuity', { n: READINESS.continuityDays });
  if (id === 'outcome') return t('budget.check.outcome', { metric, pct: fmtPct(READINESS.outcomeCoverage, 0) });
  if (id === 'scope') return t('budget.check.scope');
  return t('budget.check.currency');
};

const scopeText = (state: 'ok' | 'notConfirmed' | 'changed', t: TFn): string => {
  if (state === 'ok') return t('budget.check.scopeOk');
  if (state === 'notConfirmed') return t('budget.check.scopeNotConfirmed');
  return t('budget.check.scopeChanged');
};

/** Each check's measured value, as the prototype prints it: "38 สัปดาห์", "ยังไม่นำเข้า", "THB · USD". */
const checkValue = (c: ReadinessCheck, t: TFn): string => {
  switch (c.id) {
    case 'history':
    case 'overlap':
      return t('budget.check.weeks', { n: c.weeks });
    case 'channels':
      return c.channels.length > 0 ? names(c.channels) : t('budget.check.none');
    case 'continuity':
      return c.gapChannels.length === 0 ? t('budget.pass') : t('budget.check.gaps', { names: names(c.gapChannels) });
    case 'outcome':
      // A tenth of a percent, floored as T12h's tiles are: rounded, 188 of 209 weeks (89.95%) would read "90.0%" by its ✕.
      return c.imported
        ? t('budget.check.coverage', { weeks: c.weeks, total: c.total, pct: fmtPct(c.total > 0 ? floorAt(c.weeks / c.total, 3) : null, 1) })
        : t('budget.check.notImported');
    case 'scope':
      return scopeText(c.state, t);
    case 'currency':
      return c.currencies.length > 0 ? c.currencies.join(' · ') : DASH;
  }
};

export const metricLabel = (m: OutcomeMetric, t: TFn): string => {
  if (m === 'revenue') return t('budget.metric.revenue');
  if (m === 'leads') return t('budget.metric.leads');
  if (m === 'bookings') return t('budget.metric.bookings');
  return t('budget.metric.sales');
};

interface Tile { id: 'r2' | 'mape' | 'stability' | 'latest'; label: string; value: string; detail?: string; red: boolean }

/**
 * T12h: each figure rounds the one way that can never print it across its threshold (R² and max r-hat down, MAPE up),
 * so 0.799 reads 0.79 on a red tile. Stepped on the decimal string: x × 100 reads 0.57 as 56.99….
 * /admin/mmm-models' version list prints with these too, so it never disagrees with box 1.
 */
export const floorAt = (x: number, digits: number): number => {
  const near = Number(x.toFixed(digits));
  return near > x ? near - 10 ** -digits : near;
};
export const ceilAt = (x: number, digits: number): number => {
  const near = Number(x.toFixed(digits));
  return near < x ? near + 10 ** -digits : near;
};

/** Spec C9's operators on QUALITY's fields: red exactly where qualityOk fails. */
const tilesOf = (d: Diagnostics, dataEnd: string | null, t: TFn): Tile[] => {
  const unstable = d.max_rhat >= QUALITY.maxRhat || d.divergences > QUALITY.maxDivergences;
  const rhat = floorAt(d.max_rhat, 2).toFixed(2);
  return [
    { id: 'r2', label: t('budget.status.tile.r2'), value: floorAt(d.r2, 2).toFixed(2), red: d.r2 < QUALITY.minR2 },
    // A tenth of a percent: the fraction's third decimal.
    { id: 'mape', label: t('budget.status.tile.mape'), value: fmtPct(ceilAt(d.mape, 3), 1), red: d.mape > QUALITY.maxMape },
    {
      id: 'stability', label: t('budget.status.tile.stability'), value: unstable ? t('budget.fail') : t('budget.pass'),
      detail: t('budget.status.stabilityDetail', { rhat, n: d.divergences }), red: unstable,
    },
    { id: 'latest', label: t('budget.status.tile.latest'), value: dataEnd === null ? DASH : fmtShortDate(dataEnd, t), red: false },
  ];
};

const QualityTiles: React.FC<{ quality: Diagnostics; dataEnd: string | null }> = ({ quality, dataEnd }) => {
  const { t } = useTranslation();
  const tiles = tilesOf(quality, dataEnd, t);
  return (
    <>
      <dl className="mt-4 grid grid-cols-2 gap-3 md:grid-cols-4">
        {tiles.map((tile) => {
          const tone = tile.red ? 'bad' : tile.id === 'latest' ? 'neutral' : 'good';
          return (
            <div key={tile.id} data-tile={tile.id} data-tone={tone} className="min-w-0 rounded-xl border border-neutral-200 p-3">
              <dt className="text-xs font-medium text-neutral-700">{tile.label}</dt>
              <dd className={`mt-1 break-words text-lg font-semibold tabular-nums ${TONE_CLASS[tone]}`}>
                {/* As the check rows: a pass or a fail reads without its colour. */}
                {tone !== 'neutral' && <><span data-tile-mark>{tile.red ? '✕' : '✓'}</span>{' '}</>}
                <span data-tile-value>{tile.value}</span>
              </dd>
              {tile.detail !== undefined && <dd data-tile-detail className="mt-0.5 text-xs text-neutral-700">{tile.detail}</dd>}
            </div>
          );
        })}
      </dl>
      {tiles.some((tile) => tile.red) && (
        <p data-dont-decide className="mt-2 text-sm font-semibold text-error-700">{t('budget.status.dontDecide')}</p>
      )}
    </>
  );
};

interface SelectProps {
  metric: OutcomeMetric;
  enabled: boolean;
  /** No config yet, to one who can confirm it: "ยืนยัน scope ก่อน". A viewer cannot, so it is not said to them. */
  confirmFirst: boolean;
  onMetric: (m: OutcomeMetric) => Promise<void>;
}

/** The outcome the model uses; it writes at once, and only once a config exists (plan R3). */
const MetricSelect: React.FC<SelectProps> = ({ metric, enabled, confirmFirst, onMetric }) => {
  const { t } = useTranslation();
  const id = useId();
  const [saving, setSaving] = useState(false);
  const [failed, setFailed] = useState(false);

  const choose = async (m: OutcomeMetric): Promise<void> => {
    setSaving(true);
    setFailed(false);
    try {
      await onMetric(m);
    } catch {
      setFailed(true);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mt-4 flex flex-wrap items-center gap-2 text-sm">
      <label htmlFor={id} className="text-neutral-800">{t('budget.status.metricLabel')}</label>
      <select
        id={id} value={metric} disabled={!enabled || saving}
        onChange={(e) => { const m = OUTCOME_METRICS.find((x) => x === e.target.value); if (m) void choose(m); }}
        className={SELECT_CLASS}
      >
        {OUTCOME_METRICS.map((m) => <option key={m} value={m}>{metricLabel(m, t)}</option>)}
      </select>
      {confirmFirst && <span data-confirm-first className="text-xs text-neutral-700">{t('budget.status.confirmFirst')}</span>}
      {failed && <p role="alert" className="w-full text-sm font-medium text-error-700">{t('budget.saveFailed')}</p>}
    </div>
  );
};

/** Box I's names for the ids a 409 lists; an id the box does not know reads as the route sent it. */
const failingNames = (ids: readonly string[], checks: readonly ReadinessCheck[], t: TFn): string =>
  ids.map((id) => {
    const known = checks.find((c) => c.id === id);
    return known ? checkName(known.id, t) : id;
  }).join(' · ');

interface Refused { message: string; failing: readonly string[] }

interface DownloadProps {
  enabled: boolean;
  checks: readonly ReadinessCheck[];
  /** The outcome the file holds, which the client-data line names. */
  metric: OutcomeMetric;
  onDownload: () => Promise<void>;
}

/** "ดาวน์โหลดข้อมูลสำหรับเทรน" beside the client-data line; a refusal in the route's words, its checks by name. */
const DatasetDownload: React.FC<DownloadProps> = ({ enabled, checks, metric, onDownload }) => {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);
  const [refused, setRefused] = useState<Refused | null>(null);

  const download = async (): Promise<void> => {
    setBusy(true);
    setRefused(null);
    try {
      await onDownload();
    } catch (e) {
      setRefused({ message: e instanceof Error ? e.message : String(e), failing: e instanceof DownloadRefused ? e.failing : [] });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-download className={PART_CLASS}>
      <button type="button" disabled={!enabled || busy} onClick={() => void download()} className={BUTTON_CLASS}>
        {t('budgetAdmin.download.button')}
      </button>
      <p className="mt-2 text-xs text-neutral-700">{clientDataLine(metric, t)}</p>
      {refused !== null && (
        <div role="alert" className="mt-2 rounded-lg border border-error-700 px-3 py-2 text-sm text-error-700">
          <p className="font-semibold">{t('budgetAdmin.download.failed')}</p>
          <p className="whitespace-pre-wrap break-words">{refused.message}</p>
          {refused.failing.length > 0 && (
            <p data-download-failing>{t('budget.download.failing', { checks: failingNames(refused.failing, checks, t) })}</p>
          )}
        </div>
      )}
    </div>
  );
};

/** The seven checks, each ✓ / ✕ with its value: box 1's list, which /admin/mmm-models shows too. */
export const ReadinessChecks: React.FC<{ checks: readonly ReadinessCheck[]; metric: OutcomeMetric }> = ({ checks, metric }) => {
  const { t } = useTranslation();
  return (
    <ul className="divide-y divide-neutral-100 text-sm">
      {checks.map((c) => (
        <li key={c.id} data-check={c.id} data-pass={String(c.pass)} className="flex justify-between gap-3 py-1.5">
          <span>
            <span data-check-mark className={c.pass ? 'text-success-700' : 'text-error-700'}>{c.pass ? '✓' : '✕'}</span>
            {' '}
            <span data-check-label className="text-neutral-900">{checkLabel(c.id, metric, t)}</span>
          </span>
          <span data-check-value className="text-right text-neutral-700">{checkValue(c, t)}</span>
        </li>
      ))}
    </ul>
  );
};

interface Props {
  /** checksOf(…), in check order. */
  checks: readonly ReadinessCheck[];
  /** The config's metric, or revenue before a confirm (plan R3). */
  metric: OutcomeMetric;
  hasConfig: boolean;
  canEdit: boolean;
  onMetric: (m: OutcomeMetric) => Promise<void>;
  /** The active version's diagnostics; null = no version, so no tiles. */
  quality: Diagnostics | null;
  /** "Meridian · เทรนเมื่อ … · N สัปดาห์ · K channel + baseline" (spec §4.2), built by the page. */
  versionLine: string | null;
  /** The data's last day, the version's window end + 6 days (spec §5.12). */
  dataEnd: string | null;
  /** Plan R16. */
  stale: boolean;
  /** The scope's one currency; null without one. */
  currency: string | null;
  /** downloadAllowed(…): the route's own rule (spec §5.1). The button shows for every role (O13). */
  canDownload: boolean;
  /** Fetches and saves the dataset. A refusal rejects: its message shows, and a DownloadRefused's failing ids by name. */
  onDownload: () => Promise<void>;
  /** The upload form for canEdit (O14); null for every other role. */
  uploadSlot: ReactNode | null;
  /**
   * The version the upload stored, while it is the active one; null otherwise. The page keeps it (I1): the re-read
   * after a 201 redraws this card, and the form's own line goes with it.
   */
  stored: number | null;
}

export const ReadinessCard: React.FC<Props> = ({
  checks, metric, hasConfig, canEdit, onMetric, quality, versionLine, dataEnd, stale, currency, canDownload, onDownload, uploadSlot,
  stored,
}) => {
  const { t } = useTranslation();
  return (
    <OverviewCard id="budget-1" title={t('budget.status.title')}>
      <p className="-mt-2 mb-2 text-xs text-neutral-700">{t('budget.status.subtitle')}</p>
      <ReadinessChecks checks={checks} metric={metric} />
      {quality !== null && <QualityTiles quality={quality} dataEnd={dataEnd} />}
      {versionLine !== null && <p data-version-line className="mt-2 text-xs text-neutral-700">{versionLine}</p>}
      {stale && <p data-stale className="mt-2 text-sm font-medium text-warning-700">{t('budget.status.stale')}</p>}
      <MetricSelect metric={metric} enabled={canEdit && hasConfig} confirmFirst={canEdit && !hasConfig} onMetric={onMetric} />
      {metric === 'revenue' && currency !== null && (
        <p data-revenue-currency className="mt-1 text-xs text-neutral-700">{t('budget.status.revenueCurrency', { currency })}</p>
      )}
      <DatasetDownload enabled={canDownload} checks={checks} metric={metric} onDownload={onDownload} />
      {uploadSlot !== null && (
        <div className={PART_CLASS}>
          <h4 className="mb-2 text-sm font-semibold text-ink-900">{t('budgetAdmin.upload.title')}</h4>
          {uploadSlot}
          {stored !== null && (
            <p data-stored role="status" className="mt-3 text-sm font-medium text-success-700">
              {t('budgetAdmin.upload.stored', { version: stored })}
            </p>
          )}
        </div>
      )}
    </OverviewCard>
  );
};
